import { CommonModule as AngularCommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, ModuleWithProviders, NgModule, Type } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { EllipsisModule } from 'ngx-ellipsis';
import { ChipsComponent, ChipsFilterComponent, ContentCardComponent, EventRegistrationButtonsComponent, ExportCalendarDialogComponent, FileIconComponent, IconButtonComponent, LearnersEventDialogComponent, MaterialFileComponent, ProgressComponent, SessionContextStatusBannerComponent, SkeletonLoaderComponent, SocialWallCardComponent, SocialWallComponent, SocialWallDetailDialogComponent, SocialWallReplyComponent, SvgIconComponent, SwiperComponent, UserAvatarComponent, VideoComponent, VimeoPlayerComponent, YoutubePlayerComponent } from './components';
import { AudioWaveformPlayerComponent } from './components/audio-waveform-player/audio-waveform-player.component';
import { IconComponent } from './components/icon/icon.component';
import { ListLayoutSwitcherComponent } from './components/list-layout-switcher/list-layout-switcher.component';
import { bundleTypeTextMap, dateFormats, PageBlockKeysMap, phosphorIconList, roundnessLevelMap } from './constants';
import { API_URL, BUNDLE_TYPE_TEXT_MAP, DATE_FORMATS, REGEX_LIST, SETTINGS_SERVICE, STORAGE_KEYS, PAGE_BLOCK_KEYS_MAP, PHOSPHOR_ICON_LIST, API_CORE_APP_URL, OVERRIDE_ICONS_FROM_SETTINGS, ROUNDNESS_LEVEL_CLASSES_MAP } from './constants/injection-tokens';
import { regexList } from './constants/regex-list.const';
import { storageKeys } from './constants/storage-keys';
import { AutoTextDirectionDirective, BlockBackgroundDirective, CardBackgroundDirective, CropImageDirective, DisplayMenuItemDirective, MediaBackgroundDirective, NativeAppConditionalVisibilityDirective, NgIfFeatureDirective, PaddingDirective, SizeableDirective, SectionMinHeightDirective, SlideBackgroundDirective, SwiperContentDirective, TextImageMaskDirective, HtmlContentFontLoaderDirective, TextShadowDirective, BorderDirective, RoundnessClassDirective, ExportCalendarButtonDirective, NgSelectValueClickDirective } from './directives';
import { PlaceholderImageDirective } from './directives/placeholder-image.directive';
import { RtlDirective } from './directives/rtl.directive';
import { BrowserStorage } from './models/browser-storage';
import { BundleLabelPipe, ContrastTextColorPipe, CropImagePipe, DateWithoutTimezonePipe, EntityBundleIconPipe, EventLabelPipe, MinutesToTimePipe, NumberFormatPipe, OfflineActivityImagePipe, PreSignedUrlPipe, SafeHtmlPipe, SafeUrlPipe, SanitizeHtmlPipe, SecondsToTimePipe, SettingsMediaPresignedUrl, TimeElapsedPipe, VideoMediaPresignPipe } from './pipes';
import { CustomDatePipe } from './pipes/custom-date.pipe';
import { BaseSettingsGetService, DefaultCookieConsentProvider, LocalStorageService, OneTrustCookieConsentProvider } from './services';
import { AreaAccessDirective } from './directives/area-access.directive';
import { OperationAccessDirective } from './directives/operation-access.directive';
import { HasAccessDirective } from './directives/has-access.directive';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialogModule } from '@angular/material/dialog';
import { AudioPlayerV2Component } from '../audio-player-v2/components/audio-player-v2/audio-player-v2.component';
import { MatSliderModule } from '@angular/material/slider';

const COMPONENTS = [
  VideoComponent,
  VimeoPlayerComponent,
  YoutubePlayerComponent,
  IconComponent,
  ListLayoutSwitcherComponent,
  SkeletonLoaderComponent,
  ChipsComponent,
  AudioWaveformPlayerComponent,
  SvgIconComponent,
  UserAvatarComponent,
  FileIconComponent,
  MaterialFileComponent,
  FileIconComponent,
  SwiperComponent,
  ProgressComponent,
  ContentCardComponent,
  ChipsFilterComponent,
  SessionContextStatusBannerComponent,
  ExportCalendarDialogComponent,
  IconButtonComponent,
  EventRegistrationButtonsComponent,
  LearnersEventDialogComponent,
  SocialWallComponent,
  SocialWallCardComponent,
  SocialWallDetailDialogComponent,
  SocialWallReplyComponent,
  AudioPlayerV2Component
];

const DIRECTIVES = [
  CropImageDirective,
  SlideBackgroundDirective,
  CardBackgroundDirective,
  MediaBackgroundDirective,
  BlockBackgroundDirective,
  AutoTextDirectionDirective,
  TextImageMaskDirective,
  PaddingDirective,
  NativeAppConditionalVisibilityDirective,
  PlaceholderImageDirective,
  SectionMinHeightDirective,
  SwiperContentDirective,
  DisplayMenuItemDirective,
  NgIfFeatureDirective,
  AreaAccessDirective,
  OperationAccessDirective,
  HasAccessDirective,
  SizeableDirective,
  HtmlContentFontLoaderDirective,
  TextShadowDirective,
  BorderDirective,
  RoundnessClassDirective,
  ExportCalendarButtonDirective,
  NgSelectValueClickDirective
];

const PIPES = [
  SafeHtmlPipe,
  PreSignedUrlPipe,
  CustomDatePipe,
  CropImagePipe,
  SecondsToTimePipe,
  RtlDirective,
  VideoMediaPresignPipe,
  SanitizeHtmlPipe,
  MinutesToTimePipe,
  SettingsMediaPresignedUrl,
  EventLabelPipe,
  BundleLabelPipe,
  SafeUrlPipe,
  EntityBundleIconPipe,
  ContrastTextColorPipe,
  NumberFormatPipe,
  OfflineActivityImagePipe,
  DateWithoutTimezonePipe,
  TimeElapsedPipe
]

const MODULES = [
  AngularCommonModule,
  MatIconModule,
  MatButtonModule,
  MatTooltipModule,
  MatProgressSpinnerModule,
  MatProgressBarModule,
  MatMenuModule,
  MatDialogModule,
  MatSliderModule,
  TranslateModule,
  EllipsisModule
]

@NgModule({
  declarations: [
    ...COMPONENTS,
    ...DIRECTIVES,
    ...PIPES
  ],
  imports: [
    ...MODULES
  ],
  exports: [
    ...COMPONENTS,
    ...DIRECTIVES,
    ...PIPES,
    ...MODULES
  ],
  providers: [
    DefaultCookieConsentProvider,
    OneTrustCookieConsentProvider,
    SettingsMediaPresignedUrl
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SharedLibraryModule {
  constructor(matIconRegistry: MatIconRegistry, domSanitizer: DomSanitizer) {
    const icons = [
      'edit', 'card-portrait', 'card-landscape', 'bin', 'microphone', 'handle'
    ];

    icons.forEach(key => {
      matIconRegistry.addSvgIcon(
        `lms-${key}`,
        domSanitizer.bypassSecurityTrustResourceUrl(`/library-explorer/assets/icons/${key}.svg`)
      );
    });
  }

  public static forRoot(config: { apiUrl: string, apiCoreAppUrl: string, iconOverrideFromSettings?: boolean, settingsClassProvider: Type<BaseSettingsGetService<any>>}): ModuleWithProviders<SharedLibraryModule> {

    return {
      ngModule: SharedLibraryModule,
      providers: [
        {
          provide: SETTINGS_SERVICE,
          useExisting: config.settingsClassProvider
        },
        {
          provide: REGEX_LIST,
          useValue: regexList
        },
        {
          provide: STORAGE_KEYS,
          useValue: storageKeys
        },
        {
          provide: BrowserStorage,
          useClass: LocalStorageService
        },
        {
          provide: API_URL,
          useValue: config.apiUrl
        },
        {
          provide: API_CORE_APP_URL,
          useValue: config.apiCoreAppUrl
        },
        {
          provide: OVERRIDE_ICONS_FROM_SETTINGS,
          useValue: config.iconOverrideFromSettings
        },
        {
          provide: DATE_FORMATS,
          useValue: dateFormats
        },
        {
          provide: BUNDLE_TYPE_TEXT_MAP,
          useValue: bundleTypeTextMap
        },
        {
          provide: PAGE_BLOCK_KEYS_MAP,
          useValue: PageBlockKeysMap
        },
        {
          provide: ROUNDNESS_LEVEL_CLASSES_MAP,
          useValue: roundnessLevelMap
        },
        {
          provide: PHOSPHOR_ICON_LIST,
          useValue: phosphorIconList
        }
      ]
    };
  }
}
